<template>
  <div class="statistics-pg">
    <Search />
    <v-container>
      <div class="breadcrumb-wrap">
        <v-breadcrumbs :items="breadcrumbs" />
      </div>
      <div class="position">
        <div class="bg-white p-20">
          <h1 class="title-pg">{{ $t('Статистика по разработчикам проектов НПА') }}</h1>

          <v-row>
            <v-col cols="12" md="4">
              <label>{{ $t('Вид проекта НПА') }}</label>
              <v-select
                :items="refNpaView"
                item-value="id"
                item-text="transName"
                v-model="filters.view"
                outlined
                dense
                clearable
              />
            </v-col>
            <v-col cols="12" md="4">
              <label>{{ $t('Период размещения') }}</label>
              <v-select
                :items="periods"
                item-value="id"
                item-text="value"
                v-model="filters.period"
                outlined
                dense
                clearable
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-end">
              <button class="btn btn-default blue mr-2" @click="applyFilters">
                {{ $t('Применить') }}
              </button>
              <button class="btn btn-default" @click="resetFilters">
                {{ $t('Сбросить') }}
              </button>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4" class="date">
              <label>{{ $t('Дата размещения: с') }}</label>
              <v-menu
                v-model="menuPublishDateFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="filters.publishDateFrom"
                    readonly
                    outlined
                    dense
                    clearable
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.publishDateFrom"
                  :weekday-format="getDay"
                  :month-format="getMonthFormat"
                  :header-date-format="getHeaderDateFormat"
                  no-title
                  first-day-of-week="1"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="4" class="date">
              <label>{{ $t('Дата размещения: по') }}</label>
              <v-menu
                v-model="menuPublishDateTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="filters.publishDateTo"
                    readonly
                    outlined
                    dense
                    clearable
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.publishDateTo"
                  :weekday-format="getDay"
                  :month-format="getMonthFormat"
                  :header-date-format="getHeaderDateFormat"
                  no-title
                  first-day-of-week="1"
                />
              </v-menu>
            </v-col>
          </v-row>

          <div class="table-overflow">
            <table>
              <thead>
                <tr>
                  <td
                    v-for="(item, index) in headerTable"
                    :key="index"
                  >
                    <Sorting
                      v-if="item.sorting"
                      :item="item"
                      :currentOrderKey="sortKey"
                      @order="sortData($event)"
                      @cancelSorting="cancelSorting()"
                    />
                    <span v-else>
                      {{ item.name }}
                    </span>
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in statistics" :key="index">
                  <td>{{ item.developer }}</td>
                  <td>
                    <router-link
                      v-if="item.npas > 0"
                      :to="getSearchRoute(item, 'npas')"
                    >
                      {{ item.npas }}
                    </router-link>
                    <span v-else>{{ item.npas }}</span>
                  </td>
                  <td>
                    <router-link
                      v-if="item.comments > 0"
                      :to="getSearchRoute(item, 'comments')"
                    >
                      {{ item.comments }}
                    </router-link>
                    <span v-else>{{ item.comments }}</span>
                  </td>
                  <td>
                    <router-link
                      v-if="item.answers > 0"
                      :to="getSearchRoute(item, 'answers')"
                    >
                      {{ item.answers }}
                    </router-link>
                    <span v-else>{{ item.answers }}</span>
                  </td>
                  <td>
                    <router-link
                      v-if="item.expiredAnswers > 0"
                      :to="getSearchRoute(item, 'expiredAnswers')"
                    >
                      {{ item.expiredAnswers }}
                    </router-link>
                    <span v-else>{{ item.expiredAnswers }}</span>
                  </td>
                  <td>
                    <router-link
                      v-if="item.commentsWithoutAnswers > 0"
                      :to="getSearchRoute(item, 'commentsWithoutAnswers')"
                    >
                      {{ item.commentsWithoutAnswers }}
                    </router-link>
                    <span v-else>{{ item.commentsWithoutAnswers }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import Search from '@/components/Search'
import { mapMutations } from 'vuex'
import { Trans } from "@/plugins/Translation"
import mixin from '@/plugins/mixin/mixin'
import Sorting from "@/components/app/Sorting"
import PortalStatistics from "@/model/portalStatistic"
import CMS from '@/model/cms'

export default {
  mixins: [mixin],
  components: {
    Search,
    Sorting,
  },
  data: () => ({
    breadcrumbs: [],
    statistics: [],
    sortKey: 'npas',
    sortDir: 'desc',
    refNpaView: [],
    filters: {
      view: null,
      publishDateFrom: null,
      publishDateTo: null,
      period: null,
    },
    periods: [],
    menuPublishDateFrom: false,
    menuPublishDateTo: false,
  }),
  computed: {
    headerTable() {
      const fields = [
        { name: this.$t('Разработчик'), key: 'developer', sorting: false },
        { name: this.$t('Количество размещенных проектов НПА'), key: 'npas', sorting: true },
        { name: this.$t('Количество  комментариев к размещенным проектам'), key: 'comments', sorting: true },
        { name: this.$t('Количество  ответов к комментариям'), key: 'answers', sorting: true },
        { name: this.$t('Количество ответов с нарушением сроков'), key: 'expiredAnswers', sorting: true },
        { name: this.$t('Количество неотвеченных комментариев'), key: 'commentsWithoutAnswers', sorting: true },
      ]
      return fields
    }
  },
  metaInfo() {
    return {
      title: this.$i18n.t('DeveloperStatisticsTitleSEO'),
      meta: [{
        vmid: 'description',
        name: 'description',
        content: this.$i18n.t('DeveloperStatisticsDescriptionSEO'),
      }, {
        vmid: 'keywords',
        name: 'keywords',
        content: this.$i18n.t('DeveloperStatisticsKeywordsSEO'),
      }]
    }
  },
  async mounted() {
    await this.fetchData()
    this.scrollToTop()
  },
  methods: {
    ...mapMutations(['setLoading']),
    async fetchData() {
      this.setLoading(true)
      this.breadcrumbs = [
        { text: this.$i18n.t('Главная'), disabled: false, href: Trans.currentLocaleUrl + '/' },
        { text: this.$i18n.t('Статистика портала'), disabled: false, href: Trans.currentLocaleUrl + '/statistics' },
        { text: this.$i18n.t('Статистика по разработчикам проектов НПА'), disabled: true }
      ]
      this.periods = [
        { id: '-1 day', value: this.$i18n.t('1 день') },
        { id: '-1 week', value: this.$i18n.t('1 неделя') },
        { id: '-1 month', value: this.$i18n.t('1 месяц') },
        { id: '-1 year', value: this.$i18n.t('1 год') },
        { id: 'all', value: this.$i18n.t('все время') },
      ]

      try {
        const response = await CMS.getNpaData(['refNpaView'])
        this.refNpaView = response.data.refNpaView || []
        await this.getStatistics()
      } catch (error) {
        this.$router.push(Trans.i18nRoute({
          name: 'error',
          params: {
            status: error.response.status
          }
        }))
      } finally {
        this.setLoading(false)
      }
    },
    getApiFilters() {
      const filters = {
        sortKey: this.sortKey,
        sortDir: this.sortDir
      }

      if (this.filters.view) {
        filters.view = this.filters.view
      }
      if (this.filters.publishDateFrom) {
        filters.publishDateFrom = this.filters.publishDateFrom
      }
      if (this.filters.publishDateTo) {
        filters.publishDateTo = this.filters.publishDateTo
      }
      if (this.filters.period) {
        filters.period = this.filters.period
      }

      return filters
    },
    async getStatistics() {
      this.setLoading(true)
      try {
        const response = await PortalStatistics.developerStatistics(this.getApiFilters())
        this.statistics = response.data
      } catch (error) {
        this.$router.push(Trans.i18nRoute({
          name: 'error',
          params: {
            status: error.response.status
          }
        }))
      } finally {
        this.setLoading(false)
      }
    },
    applyFilters() {
      this.getStatistics()
    },
    resetFilters() {
      this.filters = {
        view: null,
        publishDateFrom: null,
        publishDateTo: null,
        period: null,
      }
      this.getStatistics()
    },
    getSearchQueryBase(item) {
      const query = {
        developer: item.developerId,
      }

      if (this.filters.view) {
        query.view = this.filters.view
      }
      if (this.filters.publishDateFrom) {
        query.publishDateFrom = this.filters.publishDateFrom
      }
      if (this.filters.publishDateTo) {
        query.publishDateTo = this.filters.publishDateTo
      }
      if (this.filters.period) {
        query.period = this.filters.period
      }

      return query
    },
    getSearchRoute(item, metric) {
      const query = this.getSearchQueryBase(item)

      if (metric === 'comments') {
        query.hasComments = 1
      } else if (metric === 'answers') {
        query.hasAnswers = 1
      } else if (metric === 'expiredAnswers') {
        query.hasExpiredAnswers = 1
      } else if (metric === 'commentsWithoutAnswers') {
        query.hasUnansweredComments = 1
      }

      return this.$i18nRoute({
        name: 'search-npa',
        query,
      })
    },
    sortData(sort) {
      this.sortKey = sort.key
      this.sortDir = sort.value
      this.getStatistics()
    },
    sortingFilterHandler() {
      this.getStatistics()
    },
    // Прокручивание к началу страницы
    scrollToTop() {
      window.scrollTo(0, 0)
    },
    cancelSorting() {
      this.sortKey = 'npas'
      this.sortDir = 'desc'
      this.getStatistics()
    }
  }
}
</script>
